import classnames from 'classnames';

import { rewriteImageSrc } from '@/utils/rewriteImageSrc';

import type { AuthorImagesElementProps, TAuthorImagesAttributes } from './AuthorImages.types';

// The authorImages slider tops out at 100px, so a size we can't read in pixels gets that.
const FALLBACK_AVATAR_SIZE_PX = 100;
const AVATAR_PIXEL_DENSITY = 3;
// The Studio slider moves one pixel at a time, so sizes snap to steps to keep CDN variants few.
const AVATAR_SIDE_STEP_PX = 32;

const getAvatarImageSide = (size: TAuthorImagesAttributes['size']) => {
  const sizePx = Number(String(size).replace(/px$/, ''));
  const renderedSizePx = Number.isFinite(sizePx) && sizePx > 0 ? sizePx : FALLBACK_AVATAR_SIZE_PX;

  return String(Math.ceil((renderedSizePx * AVATAR_PIXEL_DENSITY) / AVATAR_SIDE_STEP_PX) * AVATAR_SIDE_STEP_PX);
};

export function AuthorImagesElement(props: AuthorImagesElementProps) {
  const { element, children, attributes, className } = props;
  const { size, fill, authors } = element.attrs || {};
  const avatarImageSide = getAvatarImageSide(size);

  return (
    <div className={classnames(className, 'relative')} {...attributes}>
      <div className="flex -space-x-1.5">
        {authors
          ?.map((author) => ({
            id: author.id,
            name: author.name,
            imgSrc: rewriteImageSrc(author?.profile_picture?.url, {
              format: 'auto',
              width: avatarImageSide,
              height: avatarImageSide,
              // Center crop like object-cover, and unlike fit=cover it never enlarges a small original.
              fit: 'crop',
              onerror: 'redirect',
            }),
          }))
          .map(({ id, name, imgSrc }) => {
            return (
              <div
                key={id}
                className={classnames('rounded-full overflow-hidden')}
                style={{
                  width: size,
                  height: size,
                  border: '1px solid',
                  borderColor: fill,
                }}
              >
                <img
                  className={classnames('block object-cover rounded-full')}
                  src={imgSrc}
                  alt={name}
                  fetchPriority="low"
                  style={{
                    width: size,
                    height: size,
                  }}
                />
              </div>
            );
          })}
      </div>
      {children}
    </div>
  );
}
